import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import * as HttpStatus from 'http-status-codes'
import { constant, isNil, isString, negate, times } from 'lodash'

import { API_ROOT } from './config'
import { BadRequestError, ForbiddenError, ServerError, TimeoutError, UnauthorizedError } from './errors'

// We need to set an empty headers object because, otherwise, the token will be shared,
// given an internal axios bug (as of 0.18). Check https://github.com/axios/axios/issues/385.
const defaultAxiosInstance = axios.create({ headers: {} })

export type ApiCaller = typeof callApi

export async function callApi<ReturnType>(
  config: AxiosRequestConfig,
  axiosInstance: AxiosInstance = defaultAxiosInstance
): Promise<ReturnType> {
  try {
    const response = await axiosInstance.request<ReturnType>({
      ...config,
      baseURL: API_ROOT,
    })
    return response.data
  } catch (error) {
    if (error.response) {
      switch (error.response.status) {
        case HttpStatus.UNAUTHORIZED:
          throw new UnauthorizedError()
        case HttpStatus.FORBIDDEN:
          throw new ForbiddenError(error.response.data?.message || '')
        case HttpStatus.BAD_REQUEST:
          throw new BadRequestError(error.response.data ? error.response.data.message : '')
        default:
          throw new ServerError()
      }
    } else {
      throw error
    }
  }
}
export const DEFAULT_PAGINATION_ENTRIES = 10

const REQUEST_WAIT_TIME_MS = [0, 1000, 2000, 3000].concat(
  times(40, constant(3000)) // 40 * 3 seconds = 2 minutes
)
const MAX_POLLING_REQUESTS = REQUEST_WAIT_TIME_MS.length

export async function pollApi<ReturnType>(
  config: AxiosRequestConfig,
  continueChecker: (response: ReturnType) => boolean,
  axiosInstance: AxiosInstance = defaultAxiosInstance
): Promise<ReturnType> {
  let lastResponse: ReturnType | undefined
  let requestsCounter = 0
  let continuePolling = true

  while (continuePolling && requestsCounter < MAX_POLLING_REQUESTS) {
    await sleep(REQUEST_WAIT_TIME_MS[requestsCounter])
    lastResponse = await callApi<ReturnType>(config, axiosInstance)
    continuePolling = continueChecker(lastResponse)
    requestsCounter++
  }

  if (continuePolling) {
    throw new TimeoutError(`Polling of ${JSON.stringify(config)} timed out.`)
  }
  return lastResponse!
}

function sleep(ms: number = 0): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function getUrlWithPathParams(baseUrl: string, ...pathParams: Array<string | number | undefined>): string {
  const normalizedPathParams = pathParams
    .filter(negate(isNil))
    .map((pathParam: string | number | undefined = '') =>
      pathParam
        .toString()
        .replace(/\/+/g, '/')
        .replace(/^\/|\/$/g, '')
    )
    .filter((pathParam) => pathParam !== '')
  const hasTrailingSlash = baseUrl.slice(-1) === '/'
  const normalizedBaseUrl = hasTrailingSlash ? baseUrl : baseUrl + '/'
  return normalizedBaseUrl.concat(normalizedPathParams.join('/'))
}

const AUTH_HEADER = 'Authorization'

export function enableApiAuthorization(token?: string | null, axiosInstance: AxiosInstance = defaultAxiosInstance) {
  if (!token) {
    return
  }
  axiosInstance.defaults.headers[AUTH_HEADER] = `Bearer ${token}`
}

export function disableApiAuthorization(axiosInstance: AxiosInstance = defaultAxiosInstance) {
  axiosInstance.defaults.headers[AUTH_HEADER] = undefined
}

export type DomainError = {
  domain: string
  message: string
}

export function extractDomainError(error: unknown) {
  if (!(error instanceof Error)) {
    return undefined
  }
  // eslint-disable-next-line dot-notation
  const { domain, message } = error?.['response']?.data

  if (!isString(domain) || !isString(message)) {
    return undefined
  }

  return {
    domain,
    message,
  }
}

export type UnvalidatedFormikData = {
  [key: string]: unknown
}
